import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, Phone, MessageCircle } from "lucide-react";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { SITE, waGeneral } from "@/config/siteConfig";
import { useQuote } from "@/context/QuoteContext";
import { trackEvent } from "@/lib/analytics";

const NAV_LINKS = [
  { to: "/kashmir-tour-packages", label: "Packages" },
  { to: "/kashmir-honeymoon-packages", label: "Honeymoon" },
  { to: "/kashmir-family-packages", label: "Family" },
  { to: "/kashmir-tour-packages#destinations", label: "Destinations" },
  { to: "/kashmir-itineraries", label: "Itineraries" },
  { to: "/about-us", label: "About" },
  { to: "/contact-us", label: "Contact" },
  { to: "/why-youll-love-us", label: "Why You'll Love Us" },
];

const Navbar = () => {
  const { openQuote } = useQuote();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 bg-[#FAF7F2]/90 backdrop-blur-md border-b border-[#0F2F24]/10" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-brand-logo" aria-label="Book Kashmir home">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-4 xl:gap-5" data-testid="nav-desktop-menu">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-sm font-medium text-[#1E2922] hover:text-[#C28519] transition-colors whitespace-nowrap">
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a href={`tel:${SITE.phoneRaw}`} data-testid="nav-call-button" onClick={() => trackEvent("phone_click", { location: "navbar" })}
            className="hidden xl:flex items-center gap-2 text-sm font-semibold text-[#0F2F24] hover:text-[#C28519] transition-colors px-2 whitespace-nowrap">
            <Phone className="h-4 w-4" /> {SITE.phoneDisplay}
          </a>
          <Button onClick={() => openQuote()} data-testid="nav-get-quote-button"
            className="hidden sm:inline-flex bg-[#0F2F24] hover:bg-[#1a4232] text-white rounded-full px-5">
            Get a Free Quote
          </Button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="nav-hamburger-button" aria-label="Open menu">
                <Menu className="h-6 w-6 text-[#0F2F24]" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-[#FAF7F2]" data-testid="nav-mobile-menu">
              <nav className="mt-8 flex flex-col gap-1">
                {NAV_LINKS.map((l) => (
                  <SheetClose asChild key={l.to}>
                    <Link to={l.to} data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                      className="rounded-md px-3 py-3 text-base font-medium text-[#1E2922] hover:bg-[#E3EAE4] transition-colors">
                      {l.label}
                    </Link>
                  </SheetClose>
                ))}
                <div className="mt-6 flex flex-col gap-3 px-3">
                  <Button onClick={() => { setOpen(false); openQuote(); }} data-testid="nav-mobile-quote-button"
                    className="bg-[#0F2F24] text-white w-full">Get a Free Quote</Button>
                  <a href={waGeneral()} target="_blank" rel="noopener noreferrer" data-testid="nav-mobile-whatsapp-button"
                    className="flex items-center justify-center gap-2 rounded-md bg-[#25D366] px-4 py-2.5 text-sm font-semibold text-white">
                    <MessageCircle className="h-4 w-4" /> WhatsApp Us
                  </a>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
